/**
 * The account API: who is signed in, and what is account-wide.
 *
 * Both sites talk to the same server: the root site to run the account portal,
 * each corpus site to know who is reading. The forms stay native to their
 * frameworks — React on the corpus site, plain DOM on the root — because two
 * thin forms in two idioms is honest. The *contract* is not two things, and
 * keeping two copies of it is how a changed field name fixes one site and
 * quietly breaks the other.
 *
 * **The study half lives in `study-client.js`.** The seam is the product's own —
 * *the root site owns the account; corpus sites own the study* — and the root
 * site proves it: it imports nine functions, all of them from this file. Before
 * the split it was also served every findings, themes, threads, packs and
 * imports call, none of which it can reach, because the root site only proxies
 * `/api/account/` for the account routes.
 *
 * Deliberately framework-free, and dependent only on its sibling `user-errors.js`
 * (every web-shared file ships beside the others): plain ES module, imported
 * directly by the root site and through a Vite alias by the corpus site. Shared
 * at build time rather than fetched across origins at runtime, so a corpus site
 * never depends on the root site being reachable to render its own sign-in.
 *
 * Every call is same-origin. Both sites proxy /api/account/ to the API, and the
 * session cookie is scoped to the parent domain, so one sign-in covers them all
 * with no CORS anywhere.
 *
 * Sharing this module stops the two clients drifting from *each other*. It does
 * nothing about drifting from the server, which is what
 * `tests/test_account_contract.py` is for — it derives the routes and field
 * names from the running app and asserts these files still say them.
 */

import { UserFacingError, responseSentence } from './user-errors.js'

/**
 * The kind a 409 carries when a save was made against a version somebody else
 * has since replaced. One client-side home, so both sites branch on one string.
 */
// standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .js file cannot reference a
// Python constant, so the single home stays the server and this mirrors it. The
// paydown is generating the vocabulary into TypeScript rather than carrying a marker
// per site -- see docs/engineering-standards.md.
export const EDIT_CONFLICT = 'edit_conflict'

/**
 * A failure carrying the server's own wording, which is always the better one,
 * or a sentence chosen from the status where the server sent none. Either way a
 * sentence for the reader (`UserFacingError`, user-errors.js).
 */
export class AccountError extends UserFacingError {
  constructor(message, status, kind, current) {
    super(message)
    this.name = 'AccountError'
    this.status = status
    this.kind = kind
    // On an edit conflict: the record as stored now, to show beside the reader's.
    this.current = current
  }
}

/**
 * The transport, exported so `study-client.js` speaks to the server exactly the
 * way this file does — same headers, same failure shape, same base path. A
 * second copy over there is how the two halves would start disagreeing about
 * what an error is.
 */
export async function call(path, options = {}) {
  const response = await fetch(`/api/account${path}`, {
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    ...options,
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new AccountError(responseSentence(body.detail, response.status), response.status, body.kind, body.current)
  }
  return response.json()
}

const send = (method) => (path, body) =>
  call(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

export const post = send('POST')
export const put = send('PUT')
export const remove = send('DELETE')

/**
 * A query string from the parameters that were actually given.
 *
 * Empty values are dropped rather than sent blank, with one exception the
 * caller has to be able to express: `imported_from: ''` means "my own findings
 * alone", which is a filter and not an absence. Sending it as an omitted
 * parameter would ask for everything, quietly putting another author's work in
 * a list the reader believes is theirs.
 */
export function query(parameters) {
  const search = new URLSearchParams()
  for (const [name, value] of Object.entries(parameters ?? {})) {
    if (value === undefined || value === null) continue
    search.set(name, String(value))
  }
  const text = search.toString()
  return text ? `?${text}` : ''
}

// --- identity ---------------------------------------------------------------

export const signIn = (email, password) =>
  post('/login', { email, password }).then((result) => result.account)

export const signOut = () => post('/logout').then(() => undefined)

/**
 * Who is signed in, or null.
 *
 * A deployment with no account database answers 503, and that is not a fault:
 * reading, search and chat all work without one. It reads as "nobody", so the
 * caller shows no account controls rather than an error nobody can act on.
 */
export async function currentAccount() {
  try {
    return (await call('/me')).account
  } catch (error) {
    if (error instanceof AccountError && error.status === 503) return null
    throw error
  }
}

/**
 * Everything `/me` says, in one request, for a page that is starting up.
 *
 * Who is signed in *and* what the deployment says about itself: the free daily
 * allowance the marketing page shows, whether sign-up is open, the Turnstile
 * site key, and the form token a sign-up form must send back. They arrive in
 * one payload because the server sends one payload — and a page that asked
 * separately would make two round trips to render halves of the same header.
 *
 * **The form token is timed from the moment this resolves**, which is why the
 * sign-up page calls it on load rather than when the form is submitted. A token
 * younger than three seconds reads as a script.
 *
 * An unreachable or account-less deployment answers `null` for the account and
 * the number, and `false` for the switch: the shape a caller can render without
 * branching on an error, and the safe direction to be wrong in — a page that
 * cannot reach the server offers no sign-up link rather than a broken one.
 */
export async function siteBootstrap() {
  try {
    const body = await call('/me')
    return {
      account: body.account ?? null,
      freeQuestionsPerDay: body.free_questions_per_day ?? null,
      signupQuestionsPerDay: body.signup_questions_per_day ?? null,
      signupOpen: body.signup_open === true,
      turnstileSiteKey: body.turnstile_site_key ?? '',
      formToken: body.form_token ?? '',
    }
  } catch {
    return {
      account: null,
      freeQuestionsPerDay: null,
      signupQuestionsPerDay: null,
      signupOpen: false,
      turnstileSiteKey: '',
      formToken: '',
    }
  }
}

/**
 * How many questions a day this deployment gives away for free.
 *
 * Kept as its own function because the corpus site's marketing copy wants
 * exactly this and nothing else. It is `siteBootstrap` narrowed, not a second
 * request.
 */
export async function freeQuestionsPerDay() {
  return (await siteBootstrap()).freeQuestionsPerDay
}

// --- creating an account ------------------------------------------------------

/**
 * Register. Answers the same whether or not the address already has an account
 * — the difference goes into the mail, which only its owner reads — so a
 * caller must not phrase the result as "created".
 *
 * `formToken` comes from `siteBootstrap()` at page load, and `marginalia` is
 * the honeypot: a hidden field a person's browser submits empty. Both are
 * required by the server; sending neither is how a script is recognised.
 */
export const register = (
  email,
  password,
  formToken,
  turnstileToken = null,
  marginalia = '',
  humanSignal = '',
) =>
  post('/register', {
    email,
    password,
    form_token: formToken,
    turnstile_token: turnstileToken,
    marginalia,
    human_signal: humanSignal,
  }).then(() => undefined)

/** Spend a verification link. Deliberately does NOT sign anybody in. */
export const verifyEmail = (token) => post('/verify', { token }).then((r) => r.email)

/** Ask for the welcome mail again. 202 whatever the address turns out to be. */
export const resendVerification = (email, turnstileToken = null) =>
  post('/verify/resend', { email, turnstile_token: turnstileToken }).then(() => undefined)

/** Ask for a reset link. 202 whatever the address turns out to be. */
export const requestPasswordReset = (email, turnstileToken = null) =>
  post('/reset/request', { email, turnstile_token: turnstileToken }).then(() => undefined)

/** Set a new password from a mailed link. Ends every existing session. */
export const confirmPasswordReset = (token, password) =>
  post('/reset/confirm', { token, password }).then((r) => r.email)

export const changePassword = (currentPassword, newPassword) =>
  post('/password', {
    current_password: currentPassword,
    new_password: newPassword,
  }).then((result) => result.account)

export const deleteAccount = (email, password) =>
  post('/delete', { email, password }).then(() => undefined)

/**
 * Claim the name this account publishes under — a root-site surface.
 *
 * Account-wide, so it lives at the root: one publishing identity covers every
 * corpus, and a reader with a different name per corpus would be two authors
 * to their own subscribers.
 */
export const setPublishingName = (authorSlug, displayName, rowVersion) =>
  put('/author-slug', {
    author_slug: authorSlug,
    display_name: displayName ?? null,
    // The account version the form was filled in against; a stale one is a 409.
    row_version: rowVersion,
  }).then((result) => result.account)

/**
 * Choose the versions a question starts from in one corpus, in order.
 *
 * Replaces that corpus's whole set. The server refuses a code it cannot serve
 * (400 `invalid_version`, naming it) rather than saving less than was chosen.
 */
export const setStartVersions = (corpus, codes, rowVersion) =>
  put('/start-versions', {
    corpus,
    codes,
    // The account version the page was loaded against; a stale one is a 409.
    row_version: rowVersion,
  }).then((result) => result.account)

/** The text versions a start set may name, per loaded corpus. */
export const availableVersions = () => call('/versions')

// --- AI engines --------------------------------------------------------------

/**
 * What this deployment offers, and what this reader has activated.
 *
 * Two halves on purpose: `available` is the catalogue, `configured` is theirs.
 * A page needs both to render "add a key" beside "you already have one".
 * `can_store_keys` is false on a deployment with no encryption secret, and the
 * page must ask it rather than assuming — offering a field that will 503 is
 * worse than not offering it.
 */
export const engines = () => call('/engines')

/**
 * Hand over a key for one engine.
 *
 * It travels once, in this call, and never comes back. What returns is the
 * updated `configured` list carrying hints only, which is why the caller
 * re-renders from the response rather than from what it just typed.
 */
export const saveEngineKey = (engineId, apiKey, model = null) =>
  put(`/engines/${encodeURIComponent(engineId)}`, {
    api_key: apiKey,
    model: model || null,
  }).then((result) => result.configured)

/** Forget a key. Answers the same whether there was one or not. */
export const forgetEngineKey = (engineId) =>
  remove(`/engines/${encodeURIComponent(engineId)}`).then((result) => result.configured)

// --- the wallet ---------------------------------------------------------------

/**
 * The balance that pays for SourceText's own Claude beyond the free allowance,
 * with the price, the minimum top-up and the newest ledger rows. Amounts are
 * øre, excluding VAT. `enabled` is false until the operator has configured
 * payments — the page then says so rather than offering a button that 503s.
 */
export const wallet = () => call('/wallet')

/**
 * Open a Stripe Checkout for `amountMinor` øre (VAT is added there) and return
 * the URL to send the reader to. The account is the signed-in one: the server
 * takes it from the session, never from this call.
 */
export const startTopUp = (amountMinor, saveCard = false) =>
  post('/wallet/topup', { amount_minor: amountMinor, save_card: saveCard }).then((result) => result.url)

/**
 * Switch automatic top-up on or off and set its amounts, in øre ex VAT. Sent
 * against the version the form loaded; a stale one is a 409 `edit_conflict`.
 */
export const setAutoRefill = (isEnabled, thresholdMinor, amountMinor, rowVersion) =>
  put('/wallet/auto-refill', {
    is_enabled: isEnabled,
    threshold_minor: thresholdMinor,
    amount_minor: amountMinor,
    row_version: rowVersion,
  }).then((result) => result.auto_refill)

// --- add-ons -------------------------------------------------------------------

/** Every add-on, with its price and this account's state in it. */
export const addons = () => call('/addons')

/**
 * Start an add-on. The first month is taken from the wallet now; a short wallet
 * is a 402 `payment_required` whose message names the amount.
 */
export const activateAddon = (code, { acceptsTerms, requestsStartNow, acceptsDpa = false }) =>
  post(`/addons/${code}/activate`, {
    accepts_terms: acceptsTerms,
    requests_start_now: requestsStartNow,
    accepts_dpa: acceptsDpa,
  })

/**
 * Change an add-on the page is showing: `cancel`, `keep` (undo a cancel) or
 * `withdraw`. Sent against the version shown; a stale one is a 409.
 */
export const changeAddon = (code, action, rowVersion) => post(`/addons/${code}/${action}`, { row_version: rowVersion })

/** One more remote-MCP connection place, now, charged for the days left this month. */
export const buyConnectionPlace = (rowVersion) => post('/addons/mcp/places/buy', { row_version: rowVersion })

/** One fewer remote-MCP connection place, from the next period. */
export const removeConnectionPlace = (rowVersion) => post('/addons/mcp/places/remove', { row_version: rowVersion })

/** The assistants connected over remote MCP, and how many may be. */
export const mcpConnections = () => call('/mcp/connections')

/** Cut one assistant off; its place is free at once. Sent against the version shown. */
export const revokeMcpConnection = (id, rowVersion) =>
  post(`/mcp/connections/${id}/revoke`, { row_version: rowVersion })

/** Stop automatic top-up and remove the saved card from the Stripe customer. */
export const forgetSavedCard = (rowVersion) =>
  post('/wallet/auto-refill/forget-card', { row_version: rowVersion }).then((result) => result.auto_refill)

// --- portability -------------------------------------------------------------

/** Per-corpus counts, for the root site — which owns the account, not the study. */
export const studyCounts = () => call('/study').then((result) => result.corpora)

/** The export is a zip, so it is fetched and handed over rather than parsed. */
export async function downloadExport() {
  const response = await fetch('/api/account/export')
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new AccountError(responseSentence(body.detail, response.status), response.status, body.kind, body.current)
  }
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `sourcetext-export-${new Date().toISOString().slice(0, 10)}.zip`
  anchor.click()
  URL.revokeObjectURL(url)
}

// --- administering other accounts ---------------------------------------------

/**
 * The operator's surface. Every call below needs `is_admin` on the session, and
 * a signed-in non-admin gets 403 with kind `forbidden` — deliberately not 401,
 * so a client never offers a sign-in form to somebody already signed in.
 *
 * It manages *accounts*, never study content. A reader's findings are theirs,
 * and the detail call reports counts per corpus for exactly that reason.
 */

/**
 * One page of accounts, filtered and sorted by the server.
 *
 * Server-side because nothing bounds how many accounts there are, and sorting
 * one page in the browser reorders the window while claiming to have sorted the
 * list. `sort` is one of `email`, `created`, `cap`; anything else falls back to
 * the default rather than reaching the query.
 */
export const adminListUsers = (parameters) =>
  call(`/admin/users${query(parameters)}`)

export const adminGetUser = (id) => call(`/admin/users/${id}`)

/**
 * Create an invited account. **The password comes back exactly once** — the
 * server stores only its hash, so a caller that does not show it has lost it,
 * and the only remedy is `adminResetPassword`.
 */
export const adminCreateUser = (email, isAdmin = false, cap = null) =>
  post('/admin/users', { email, is_admin: isAdmin, cap })

/** A new one-time password, also shown once, and every session ended. */
export const adminResetPassword = (id) => post(`/admin/users/${id}/reset-password`)

/** `null` is unmetered. Never a large number standing in for "no limit". */
export const adminSetCap = (id, cap, rowVersion) =>
  put(`/admin/users/${id}/cap`, { cap, row_version: rowVersion }).then((result) => result.user)

/**
 * Refused with 409 on your own account: another admin, or the CLI, can do it.
 * Also 409 `edit_conflict` when the row changed since this screen loaded it.
 */
export const adminSetAdmin = (id, isAdmin, rowVersion) =>
  put(`/admin/users/${id}/admin`, { is_admin: isAdmin, row_version: rowVersion }).then((result) => result.user)

/** Stamp an address proved without a mail — the support path for one that never arrived. */
export const adminVerifyEmail = (id) =>
  post(`/admin/users/${id}/verify-email`).then((result) => result.user)

/**
 * Erasure of somebody else's account. The address is typed out to confirm, the
 * same friction the reader's own delete carries — and here it also rules out
 * deleting the wrong row out of a list of fifty. Refused with 409 on your own:
 * leaving is what the account page is for, and it asks for a password.
 */
export const adminDeleteUser = (id, email) =>
  // POST, not DELETE: the typed address travels in a body, and a DELETE with a
  // body is the one request an intermediary may drop. The reader's own
  // `deleteAccount` posts for the same reason.
  post(`/admin/users/${id}/delete`, { email }).then(() => undefined)
